/**
 * Admin Clock Entries Page
 * View all clock in/out entries from all drivers in table format
 */

import { useState, useMemo } from 'react'
import { Clock, Download, MapPin } from 'lucide-react'
import { Card, Input, Button } from '../ui'
import { useAppStore } from '../../stores'
import { formatDate, formatTime, calculateHours, formatHours } from '../../utils/helpers'

interface ClockSession {
  driverId: string
  date: string
  clockIn: {
    time: string
    timestamp: string
    location: { latitude: number; longitude: number }
  } | null
  clockOut: {
    time: string
    timestamp: string
    location: { latitude: number; longitude: number }
  } | null
  totalHours: number | null
}

export function AdminClockEntries() {
  const { clockEntries } = useAppStore()
  const [searchTerm, setSearchTerm] = useState('')
  
  // Group clock entries by driver and date
  const sessions = useMemo(() => {
    const grouped = new Map<string, ClockSession>()
    
    clockEntries.forEach(entry => {
      const date = formatDate(new Date(entry.timestamp))
      const key = `${entry.driverId}-${date}`
      
      if (!grouped.has(key)) {
        grouped.set(key, {
          driverId: entry.driverId,
          date,
          clockIn: null,
          clockOut: null,
          totalHours: null
        })
      }
      
      const session = grouped.get(key)!
      if (entry.type === 'clock-in') {
        session.clockIn = {
          time: formatTime(new Date(entry.timestamp)),
          timestamp: entry.timestamp,
          location: entry.location
        }
      } else {
        session.clockOut = {
          time: formatTime(new Date(entry.timestamp)),
          timestamp: entry.timestamp,
          location: entry.location
        }
      }
      
      // Calculate total hours if both clock-in and clock-out exist
      if (session.clockIn && session.clockOut) {
        const clockInDate = new Date(session.clockIn.timestamp)
        const clockOutDate = new Date(session.clockOut.timestamp)
        session.totalHours = calculateHours(clockInDate, clockOutDate)
      }
    })
    
    return Array.from(grouped.values()).sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime()
    })
  }, [clockEntries])
  
  // Filter sessions
  const filteredSessions = sessions.filter(s => 
    s.driverId.toLowerCase().includes(searchTerm.toLowerCase())
  )
  
  const getMapUrl = (lat: number, lng: number) => {
    return `https://www.google.com/maps?q=${lat},${lng}`
  }
  
  // Export to CSV
  const handleExport = () => {
    const headers = ['No', 'Date', 'Driver ID', 'Clock In', 'Clock In Location', 'Clock Out', 'Clock Out Location', 'Total Hours']
    const rows = filteredSessions.map((s, i) => [
      i + 1,
      s.date,
      s.driverId,
      s.clockIn?.time || '-',
      s.clockIn ? `${s.clockIn.location.latitude}, ${s.clockIn.location.longitude}` : '-',
      s.clockOut?.time || '-',
      s.clockOut ? `${s.clockOut.location.latitude}, ${s.clockOut.location.longitude}` : '-',
      s.totalHours ? formatHours(s.totalHours) : '-'
    ])
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `clock-entries-${formatDate(new Date())}.csv`
    a.click()
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-medium text-gray-900">Clock Entries</h1>
          <p className="text-sm text-gray-500">{filteredSessions.length} sessions</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleExport}>
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
      </div>
      
      {/* Filters */}
      <Card className="p-4">
        <Input
          placeholder="Search by driver ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Card>
      
      {/* Table */}
      {filteredSessions.length === 0 ? (
        <Card className="text-center py-12">
          <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No clock entries found</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-600 uppercase tracking-wider">No.</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-600 uppercase tracking-wider">Date</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-600 uppercase tracking-wider">Name</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-600 uppercase tracking-wider">Clock-in</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-600 uppercase tracking-wider">Location</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-600 uppercase tracking-wider">Clock-out</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-600 uppercase tracking-wider">Location</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-600 uppercase tracking-wider">Total Hours</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSessions.map((session, index) => (
                  <tr key={`${session.driverId}-${session.date}`} className="hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-900 text-center">{index + 1}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 text-center">{session.date}</td>
                    <td className="py-3 px-4 text-sm font-medium text-gray-900 text-center">{session.driverId}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 text-center">
                      {session.clockIn ? session.clockIn.time : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-center">
                      {session.clockIn ? (
                        <a
                          href={getMapUrl(session.clockIn.location.latitude, session.clockIn.location.longitude)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                        >
                          <MapPin className="w-3 h-3" />
                          View
                        </a>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-900 text-center">
                      {session.clockOut ? session.clockOut.time : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-center">
                      {session.clockOut ? (
                        <a
                          href={getMapUrl(session.clockOut.location.latitude, session.clockOut.location.longitude)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                        >
                          <MapPin className="w-3 h-3" />
                          View
                        </a>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm font-medium text-gray-900 text-center">
                      {session.totalHours !== null ? (
                        formatHours(session.totalHours)
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}
